import type { Article } from "@/lib/insights/marketing/articles";

/**
 * Flagship article (ROI) — "The real cost of scrap".
 * Big-number stats + comparison bars + an illustrative defect-cost chart.
 * Honest, vendor-neutral; all figures generic and clearly illustrative.
 */
export const SCRAP_COST_ARTICLE: Article = {
  slug: "the-real-cost-of-scrap",
  title: "The real cost of scrap: the economics of foundry rejects",
  deck:
    "The scrap bin is the most expensive thing in most foundries — and the most under-measured. The headline rejection rate hides the part that actually hurts: everything you already spent to make a casting that never ships.",
  goal: "ROI",
  readingMins: 8,
  heroStat: { value: "5–10%", label: "typical casting scrap rate, industry-wide" },
  blocks: [
    {
      kind: "paragraph",
      lead: true,
      text:
        "Ask a foundry what its scrap costs and you will usually get a percentage — say, six percent. It sounds manageable, almost like a rounding error in a good month. But that number is deceptive in two ways. First, it is measured at the wrong point: a casting rejected at final inspection has already absorbed the melt, the mould, the labour and the energy of a good part. Second, the percentage hides the absolute money. Six percent of a year's output is not six percent of your trouble — it is often the difference between a thin margin and a healthy one.",
    },
    {
      kind: "metricRow",
      metrics: [
        {
          value: "5–10%",
          label: "Typical scrap rate",
          caption: "Casting rejects as a share of output, industry-wide.",
          tone: "danger",
        },
        {
          value: "60–80%",
          label: "Cost already sunk",
          caption: "Share of a casting's cost incurred before final inspection.",
          tone: "warning",
        },
        {
          value: "1.5–4×",
          label: "True multiplier",
          caption: "Real cost of a reject vs. its raw-material value alone.",
          tone: "brand",
        },
      ],
    },
    {
      kind: "sectionHeading",
      eyebrow: "The hidden ledger",
      heading: "A reject is not a lump of metal — it is a stack of spent inputs",
      sub: "Remelting recovers some material value. It recovers none of the conversion cost.",
    },
    {
      kind: "paragraph",
      text:
        "The comforting story about scrap is that it goes back in the furnace, so it is not really lost. The metal, partly true. The cost, not at all. By the time a casting reaches final inspection it has consumed energy to melt, sand and binder to mould, machine time, inspection time and a slot in the schedule that a good part could have used. Remelting the casting returns a fraction of the alloy value and none of the rest. The conversion cost is simply gone.",
    },
    {
      kind: "comparisonBars",
      title: "Illustrative: where the cost of one rejected casting sits",
      unit: "% of part cost",
      max: 100,
      bars: [
        { label: "Raw material (partly recoverable on remelt)", value: 35, tone: "success" },
        { label: "Melt energy (lost)", value: 18, tone: "danger" },
        { label: "Moulding: sand, binder, labour (lost)", value: 22, tone: "danger" },
        { label: "Machining + inspection time (lost)", value: 15, tone: "danger" },
        { label: "Schedule / opportunity (lost)", value: 10, tone: "warning" },
      ],
      caption:
        "Generic cost split for illustration. The lesson holds across foundries: the recoverable slice is the small one.",
    },
    {
      kind: "pullQuote",
      quote:
        "You do not pay for scrap when you weigh the bin. You paid for it upstream, in energy and hours, long before anyone called it a reject.",
      attribution: "The uncomfortable accounting of rejects",
    },
    {
      kind: "sectionHeading",
      eyebrow: "Where it concentrates",
      heading: "A few defects do most of the damage",
    },
    {
      kind: "paragraph",
      text:
        "Scrap is rarely spread evenly. In most foundries a short list of defect types — gas porosity, shrinkage, inclusions, misruns — accounts for the large majority of rejects. That concentration is good news, because it means you do not have to fix everything. If you can reliably attribute rejects to a defect type, and a defect type to the process conditions behind it, a small number of targeted changes can move the whole number.",
    },
    {
      kind: "chart",
      chart: "bar",
      title: "Illustrative: annual scrap cost by defect type",
      xKey: "defect",
      unit: "k",
      series: [{ dataKey: "cost", label: "Cost (currency, thousands)", tone: "danger" }],
      data: [
        { defect: "Porosity", cost: 142 },
        { defect: "Shrinkage", cost: 98 },
        { defect: "Inclusions", cost: 61 },
        { defect: "Misrun", cost: 34 },
        { defect: "Cold shut", cost: 21 },
        { defect: "Other", cost: 16 },
      ],
      caption:
        "Generic, illustrative figures. The Pareto shape — a couple of defects dominating — is the part worth internalising.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "The measurement trap",
      text:
        "If scrap is logged as a single monthly percentage with no link to the heat, mould or process window behind each reject, you can see that you have a problem but never why. Aggregate numbers tell you to worry; attributed numbers tell you what to change.",
    },
    {
      kind: "sectionHeading",
      eyebrow: "The opportunity",
      heading: "What a single point of scrap is worth",
    },
    {
      kind: "paragraph",
      text:
        "Run the arithmetic on your own plant and it tends to be sobering. Take annual good output, add the cost of conversion, and a single percentage point of scrap usually maps to a number with real commas in it. Crucially, scrap reduction drops almost entirely to the bottom line — you have already paid for the capacity, so a casting that ships instead of going to the bin is close to pure margin. That is why scrap is the highest-leverage cost in the building: it is large, it is concentrated, and shrinking it costs you almost nothing once you can see the cause.",
    },
    {
      kind: "statCallout",
      value: "~1 pt",
      label: "of scrap eliminated often pays for the whole monitoring effort",
      caption:
        "Illustrative, not a guarantee. Because reductions fall straight to margin, even a modest, sustained drop tends to dwarf the cost of measuring properly.",
      tone: "success",
    },
    {
      kind: "paragraph",
      text:
        "None of this requires a moon-shot. It requires measuring scrap where the cost actually accrues, attributing each reject to the conditions behind it, and acting on the two or three defects that dominate. The technology to do that is the boring, valuable part — and it is the part worth investing in before any flashier initiative.",
    },
    {
      kind: "sahasEnables",
      intro:
        "Seeing the true cost of scrap is one thing; attributing each reject to the heat and process window behind it — so you can actually shrink it — is another. That attribution is what the platform is built to make routine.",
      capabilities: [
        {
          title: "Attribute every reject",
          detail:
            "Full part genealogy ties each scrapped casting to its heat, mould and the live conditions that produced it — no manual cross-referencing.",
          icon: "search",
        },
        {
          title: "Find the dominant causes",
          detail:
            "A correlation and commonality engine surfaces which process variables actually move your worst defect, with Pareto and SPC views.",
          icon: "lineChart",
        },
        {
          title: "Quantify the win",
          detail:
            "Deterministic heat, daily and monthly reports turn 'scrap is down' into an auditable number you can take to the board.",
          icon: "coins",
        },
      ],
    },
  ],
};
